import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Empty, Flex, Form, Input, Radio, Table, Typography, message } from 'antd';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { PrintButton } from '../../components/print/PrintButton';
import { BillSaleDrawer } from '../../components/BillSaleDrawer';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { formatMoney, isMoreThan, isPositive } from '../../lib/money';
import { invalidateAfterBookingPayment } from './invalidate-booking-payment';
import { listBookingsToReceive, takeBookingPayment, type BookingToReceive, type PaymentMethod } from './api';

const { Text } = Typography;

const METHODS: PaymentMethod[] = ['CASH', 'CARD', 'BANK_TRANSFER', 'EWALLET'];

/** A reserved bike that is paid up to the invoice's ask: the next step is billing, not another payment. */
function nothingLeft(row: BookingToReceive): boolean {
  return row.kind === 'DOWN_PAYMENT' && row.stillToPay !== null && !isPositive(row.stillToPay);
}

/**
 * Bookings to receive: the deposits advisors have sent to the cashier, and
 * down payments on reserved bikes not yet billed. A sale whose registration is
 * complete is billed from its row: cashiers bill at the Till and never open
 * the booking itself.
 *
 * As at the rest of the till, the idempotency key is generated ONCE when the
 * payment drawer opens, never per click: a double tap must send the same key
 * (CLAUDE.md §9), and a deposit taken twice is a customer's money taken twice.
 */
export function BookingsToReceive({ drawerOpen }: { drawerOpen: boolean }): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [paying, setPaying] = useState<BookingToReceive | null>(null);
  const [billing, setBilling] = useState<BookingToReceive | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('CASH');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [paymentKey, setPaymentKey] = useState('');
  // What the current key was last sent with. A retry of the SAME payment
  // reuses the key; a retry with a different method, amount or reference is a
  // different payment and must not reach the server under the old key.
  const [keySentWith, setKeySentWith] = useState<string | null>(null);
  // Set once the payment is taken: the drawer stays open as a done state.
  const [taken, setTaken] = useState<{ uid: string; receiptNo: string } | null>(null);

  const list = useInfiniteQuery({
    queryKey: ['bookings-to-receive'],
    queryFn: ({ pageParam }) => listBookingsToReceive(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];

  const openUid = useRef<string | null>(null);
  useEffect(() => {
    openUid.current = paying?.bookingUid ?? null;
  }, [paying]);

  const pay = useMutation({
    mutationFn: ({ target, key }: { target: BookingToReceive; key: string }) =>
      takeBookingPayment(
        target.bookingUid,
        {
          method,
          amount: amount.trim(),
          ...(reference.trim().length > 0 ? { reference: reference.trim() } : {}),
        },
        key,
      ),
    onSuccess: (result, { target }) => {
      invalidateAfterBookingPayment(queryClient, target.bookingUid);
      // The drawer turns into a done state; the key stays until Done. Only if
      // it is still this booking's drawer.
      if (openUid.current === target.bookingUid) setTaken(result);
    },
    // The API's refusal is shown as-is: "reserved for someone else meanwhile"
    // is the cashier's cue to send the customer back to the advisor.
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('till.payFailed'));
    },
  });

  const closePaying = (): void => {
    setPaying(null);
    setTaken(null);
  };

  const startPayment = (row: BookingToReceive): void => {
    setTaken(null);
    setMethod('CASH');
    // The API sends it with two decimal places: a string, never a JS number.
    // A down payment starts at what is still to pay; the cashier lowers it for a part payment.
    setAmount(row.kind === 'DEPOSIT' ? (row.pendingDepositAmount ?? '') : (row.stillToPay ?? ''));
    setReference('');
    // Generated ONCE, here — see the component comment.
    setPaymentKey(crypto.randomUUID());
    setKeySentWith(null);
    setPaying(row);
  };

  const submit = (): void => {
    const body = JSON.stringify([method, amount.trim(), reference.trim()]);
    let key = paymentKey;
    if (keySentWith !== null && keySentWith !== body) {
      key = crypto.randomUUID();
      setPaymentKey(key);
    }
    setKeySentWith(body);
    if (paying !== null) pay.mutate({ target: paying, key });
  };

  const typed = amount.trim();
  // A plain ringgit amount above zero. Checked as a string: money is never a JS number.
  const wellFormed = /^\d+(\.\d{1,2})?$/.test(typed) && isPositive(typed);
  // The server refuses it too; saying so here saves the cashier a round trip.
  const overBalance = wellFormed && paying?.stillToPay != null && isMoreThan(typed, paying.stillToPay);
  const amountOk = wellFormed && !overBalance;

  const amountHelp = (): string | undefined => {
    const still = paying?.stillToPay ?? null;
    if (overBalance && still !== null) return t('till.bookings.overBalance', { amount: formatMoney(still) });
    if (paying?.kind === 'DEPOSIT') return t('till.bookings.amountHelp');
    return still === null ? undefined : t('till.bookings.upTo', { amount: formatMoney(still) });
  };

  const dueLabel = (row: BookingToReceive): string => {
    if (row.kind === 'DEPOSIT' && row.pendingDepositAmount !== null) {
      return t('till.bookings.deposit', { amount: formatMoney(row.pendingDepositAmount) });
    }
    if (row.stillToPay === null) return t('till.bookings.downPayment');
    return isPositive(row.stillToPay)
      ? t('till.bookings.stillToPay', { amount: formatMoney(row.stillToPay) })
      : t('till.bookings.nothingLeft');
  };

  return (
    <>
      <Card title={t('till.bookings.title')} styles={{ body: { padding: 0 } }}>
        {list.error !== null && (
          <Alert
            type="error"
            showIcon
            banner
            message={list.error instanceof ApiError ? list.error.message : t('till.bookings.loadFailed')}
          />
        )}
        <Table<BookingToReceive>
          dataSource={rows}
          rowKey={(row) => row.bookingUid}
          loading={list.isFetching}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('till.bookings.empty')} />,
          }}
          // Few, stacked columns and the action pinned right: at 390px the
          // cashier reaches Take payment without scrolling sideways.
          columns={[
            {
              title: t('till.bookings.booking'),
              key: 'booking',
              render: (_: unknown, row: BookingToReceive) => (
                <Flex vertical gap={2}>
                  <Text strong>{row.customerName}</Text>
                  <Text type="secondary" className="tabular">
                    {row.bookingNo}
                  </Text>
                </Flex>
              ),
            },
            {
              title: t('till.bookings.bike'),
              key: 'bike',
              render: (_: unknown, row: BookingToReceive) => (
                <Flex vertical gap={2}>
                  <Text>{orDash(row.bike)}</Text>
                  <Text type="secondary" className="tabular">
                    {orDash(row.chassisNo)}
                  </Text>
                </Flex>
              ),
            },
            {
              title: t('till.bookings.due'),
              key: 'due',
              render: (_: unknown, row: BookingToReceive) => (
                <Flex vertical gap={2}>
                  <Text strong>{dueLabel(row)}</Text>
                  {isPositive(row.received) && (
                    <Text type="secondary">
                      {row.saleTotal === null
                        ? t('till.bookings.received', { amount: formatMoney(row.received) })
                        : t('till.bookings.receivedOf', {
                            received: formatMoney(row.received),
                            total: formatMoney(row.saleTotal),
                          })}
                    </Text>
                  )}
                  {row.loan !== null && (
                    <Text type="secondary">{t('till.bookings.afterLoan', { amount: formatMoney(row.loan) })}</Text>
                  )}
                  {row.loanPending && <Text type="secondary">{t('till.bookings.loanPending')}</Text>}
                </Flex>
              ),
            },
            {
              title: '',
              key: 'take',
              fixed: 'right' as const,
              render: (_: unknown, row: BookingToReceive) => (
                <Flex vertical gap={8}>
                  <Button
                    type="primary"
                    size="large"
                    disabled={nothingLeft(row)}
                    onClick={() => {
                      startPayment(row);
                    }}
                  >
                    {t('till.take')}
                  </Button>
                  {row.readyToBill && (
                    <Button
                      size="large"
                      onClick={() => {
                        setBilling(row);
                      }}
                    >
                      {t('bookings.bill')}
                    </Button>
                  )}
                </Flex>
              ),
            },
          ]}
        />
        {list.hasNextPage && (
          <Flex justify="center" style={{ padding: 12 }}>
            <Button
              size="large"
              loading={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {t('till.bookings.more')}
            </Button>
          </Flex>
        )}
      </Card>

      <BillSaleDrawer
        bookingUid={billing?.bookingUid ?? ''}
        open={billing !== null}
        onClose={() => {
          setBilling(null);
        }}
        onBilled={() => {
          if (billing !== null) invalidateAfterBookingPayment(queryClient, billing.bookingUid);
        }}
      />

      <ActionDrawer
        open={paying !== null}
        title={t('till.bookings.takeFrom', { name: paying?.customerName ?? '' })}
        submitLabel={t('till.bookings.take', { amount: formatMoney(typed.length > 0 ? typed : '0') })}
        submitting={pay.isPending}
        submitDisabled={!amountOk}
        onClose={closePaying}
        onSubmit={submit}
        footer={
          taken === null ? undefined : (
            <Flex justify="flex-end">
              <Button type="primary" size="large" onClick={closePaying}>
                {t('till.done')}
              </Button>
            </Flex>
          )
        }
        consequence={taken !== null ? undefined : t('till.bookings.consequence', { bookingNo: paying?.bookingNo ?? '' })}
      >
        {taken !== null ? (
          <Flex vertical gap={16}>
            <Alert type="success" showIcon message={t('till.doneTitle', { receiptNo: taken.receiptNo })} />
            <PrintButton doc="receipt" uid={taken.uid} label={t('print.printReceipt')} type="primary" />
          </Flex>
        ) : (
        <Form layout="vertical">
          <Form.Item label={t('till.fields.method')} required>
            <Radio.Group
              value={method}
              onChange={(e) => {
                setMethod(e.target.value as PaymentMethod);
              }}
              size="large"
              buttonStyle="solid"
              optionType="button"
              options={METHODS.map((m) => ({ value: m, label: t(`till.method.${m}`) }))}
            />
          </Form.Item>

          {method === 'CASH' && !drawerOpen && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('till.noDrawer')}
              description={t('till.noDrawerHelp')}
            />
          )}

          <Form.Item
            label={t('till.fields.amount')}
            htmlFor="booking-payment-amount"
            required
            validateStatus={overBalance ? 'error' : undefined}
            help={amountHelp()}
          >
            <Input
              id="booking-payment-amount"
              inputMode="decimal"
              prefix="MYR"
              size="large"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
              }}
            />
          </Form.Item>

          {method !== 'CASH' && (
            <Form.Item label={t('till.fields.reference')} help={t('till.referenceHelp')}>
              <Input
                size="large"
                value={reference}
                onChange={(e) => {
                  setReference(e.target.value);
                }}
              />
            </Form.Item>
          )}
        </Form>
        )}
      </ActionDrawer>
    </>
  );
}
